import React from 'react'
import { Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { moment } from '@BUP/configs/i18n'
import styles from './period-dashboard.module.scss'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import AlertComponent from '@BUP/components/Generics/Alert'
import BupSpinner from '@BUP/components/Loading/Spinner'

const { Option } = Select

/**
 * Minimal counselor-facing control for the "Asignación de Cursos" preferences period: pick a
 * grade, open/reopen/close its period, see how many students have completed it.
 *
 * A period scopes exactly one grade, and different grades can have their own period open at the
 * same time (see the backend's openPeriod doc comment) - so the grade selector isn't just for
 * opening a new period, it's how the counselor picks WHICH grade's period this card shows.
 * Switching it re-queries `period` for that grade (see the parent page).
 */
const PeriodDashboard = ({
  isLoading,
  error,
  period,
  grades,
  isLoadingGrades,
  selectedGrade,
  onSelectGrade,
  onOpen,
  onClose,
  isOpening,
  isClosing
}) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })

  if (error) {
    return (
      <AlertComponent
        type='error'
        messageKey='common.resources.course-assignment.actions.read.failure'
        error={error}
      />
    )
  }

  const isOpen = period?.status === 'open'

  return (
    <div className={styles.container}>
      <div className={styles.buttons_wrapper}>
        <Select
          placeholder={t('gradeSelectPlaceholder')}
          value={selectedGrade}
          onChange={onSelectGrade}
          loading={isLoadingGrades}
          style={{ minWidth: 200 }}
        >
          {(grades || []).map(grade => <Option key={grade} value={grade}>{grade}</Option>)}
        </Select>
      </div>

      {isLoading
        ? <BupSpinner size='small' />
        : (
          <div className={styles.status_wrapper}>
            {!period && <p className={styles.status_text}>{t('noPeriod')}</p>}
            {period && (
              <>
                <p className={styles.status_text}>
                  {isOpen ? t('statusOpen') : t('statusClosed')}
                </p>
                {period.grade && (
                  <p className={styles.completion_text}>{t('periodGrade', { grade: period.grade })}</p>
                )}
                {isOpen && period.opensAt && (
                  <p className={styles.completion_text}>
                    {t('openedAt', { date: moment(period.opensAt).format('lll') })}
                  </p>
                )}
                {!isOpen && period.closedAt && (
                  <p className={styles.completion_text}>
                    {t('closedAt', { date: moment(period.closedAt).format('lll') })}
                  </p>
                )}
                <p className={styles.completion_text}>
                  {t('completionCount', {
                    completed: period.completedCount ?? 0,
                    eligible: period.eligibleCount ?? 0
                  })}
                </p>
              </>
            )}
          </div>
          )}

      <div className={styles.buttons_wrapper}>
        {isOpen
          ? (
            <ButtonComponent
              buttonText={t('closeButton')}
              colorType='primary'
              onClick={() => onClose(period.id)}
              disabled={isClosing}
            />
            )
          : (
            <ButtonComponent
              buttonText={t(period ? 'reopenButton' : 'openButton')}
              colorType='primary'
              onClick={() => onOpen(selectedGrade)}
              disabled={isOpening || !selectedGrade}
            />
            )}
      </div>
    </div>
  )
}

export default PeriodDashboard
